import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";

// Eagerly loaded — best for LCP
import { HomePage } from "./pages/Home";

// Lazy-loaded marketing pages
const PricingPage = lazy(() =>
  import("./pages/Pricing").then((m) => ({ default: m.PricingPage }))
);
const ProductsPage = lazy(() =>
  import("./pages/ProductsPage").then((m) => ({ default: m.ProductsPage }))
);
const ServicesPage = lazy(() =>
  import("./pages/ServicesPage").then((m) => ({ default: m.ServicesPage }))
);
const FaqPage = lazy(() =>
  import("./pages/Faq").then((m) => ({ default: m.FaqPage }))
);
const SupportPage = lazy(() =>
  import("./pages/Support").then((m) => ({ default: m.SupportPage }))
);
const AuthPage = lazy(() =>
  import("./pages/Auth").then((m) => ({ default: m.AuthPage }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPassword").then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPassword").then((m) => ({ default: m.ResetPasswordPage }))
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage }))
);

// Dashboard — layout and pages (all lazy)
const DashboardLayout = lazy(() =>
  import("./layouts/DashboardLayout").then((m) => ({
    default: m.DashboardLayout
  }))
);

const DashboardHome = lazy(() =>
  import("./pages/dashboard/DashboardHome").then((m) => ({
    default: m.DashboardHome
  }))
);

// These three pages use **default export** in their files
const OrdersPage = lazy(() => import("./pages/dashboard/OrdersPage"));
const ConversationsPage = lazy(
  () => import("./pages/dashboard/ConversationsPage")
);
const ProfilePage = lazy(() => import("./pages/dashboard/ProfilePage"));
const VerifyInvoicePage = lazy(() => import("./pages/VerifyInvoicePage"));
const DemosPage = lazy(() => import("./pages/Demos").then((m) => ({ default: m.DemosPage })));

const LazyLoadingFallback = () => (
  <div className="flex items-center justify-center py-16 text-sm text-gray-500">
    Loading...
  </div>
);

// Small helper to avoid repeating <Suspense> everywhere
function withSuspense(element: React.ReactElement) {
  return <Suspense fallback={<LazyLoadingFallback />}>{element}</Suspense>;
}

export default function App() {
  return (
    <Routes>
      {/* Home stays eager for best LCP */}
      <Route path="/" element={<HomePage />} />

      {/* Marketing pages */}
      <Route path="/pricing" element={withSuspense(<PricingPage />)} />
      <Route path="/products" element={withSuspense(<ProductsPage />)} />
      <Route path="/services" element={withSuspense(<ServicesPage />)} />
      <Route path="/faq" element={withSuspense(<FaqPage />)} />
      <Route path="/support" element={withSuspense(<SupportPage />)} />

      {/* Auth */}
      <Route
        path="/login"
        element={withSuspense(<AuthPage initialMode="login" />)}
      />
      <Route
        path="/signup"
        element={withSuspense(<AuthPage initialMode="signup" />)}
      />
      <Route
        path="/forgot-password"
        element={withSuspense(<ForgotPasswordPage />)}
      />
      <Route
        path="/reset-password"
        element={withSuspense(<ResetPasswordPage />)}
      />

      {/* Dashboard */}
      <Route path="/dashboard" element={withSuspense(<DashboardLayout />)}>
        <Route index element={withSuspense(<DashboardHome />)} />
        <Route path="orders" element={withSuspense(<OrdersPage />)} />
        <Route
          path="conversations"
          element={withSuspense(<ConversationsPage />)}
        />
        <Route path="profile" element={withSuspense(<ProfilePage />)} />
      </Route>

      {/* Invoice Verification */}
      <Route
        path="/verify-invoice/:invoiceId"
        element={withSuspense(<VerifyInvoicePage />)}
      />

      {/* New Demos Page */}
      <Route path="/demos" element={withSuspense(<DemosPage />)} />

      {/* 404 */}
      <Route path="*" element={withSuspense(<NotFoundPage />)} />
    </Routes>
  );
}
